import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
  useTheme,
  MenuItem,
  Select,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  Facebook as FacebookIcon,
  Apple as AppleIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomTextField from "../components/RTLTextField";
import { useDispatch } from "react-redux";
import { addNewUser } from "../redux/slices/user/thunk";

const SignUpScreen = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language == "ar";
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countryCode, setCountryCode] = useState("+966");
  const dispatch = useDispatch();

  // Form validation schema
  const validationSchema = Yup.object({
    fullName: Yup.string().required(t("validation.fullNameRequired")),
    locationurl: Yup.string()
      .url(t("validation.validUrl")),
      // .required(t("validation.locationurl")),
    address: Yup.string().required(t("validation.address")),
    email: Yup.string()
      .email(t("validation.invalidEmail"))
      .required(t("validation.emailRequired")),
    phoneNumber: Yup.string()
      // .matches(/^[0-9]+$/, t("validation.phoneNumbersOnly"))
      // .min(8, t("validation.phoneTooShort"))
      // .max(15, t("validation.phoneTooLong"))
      .required(t("validation.phoneRequired")),
    password: Yup.string()
      .min(6, t("validation.passwordMin"))
      .required(t("validation.passwordRequired")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], t("validation.passwordsMustMatch"))
      .required(t("validation.confirmPasswordRequired")),
  });

  // Formik configuration
  const formik = useFormik({
    initialValues: {
      fullName: "",
      email: "",
      address: "",
      locationurl: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      agreeToTerms: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      console.log("Sign up form submitted:", values);
      const data = {
        userName: values.fullName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        countryCode: countryCode,
        address: values.address,
        location: values.locationurl,
        password: values.password,
        type: "client",
      };

      try {
        // dispatch ثم unwrap لالتقاط الخطأ إذا فشل
        setLoading(true);
        const response = await dispatch(addNewUser({ data })).unwrap();
        // إذا وصلنا هنا، تم الإضافة بنجاح
        console.log("addNewUser succeeded, response:", response);
        navigate("/login");
        setLoading(false);
      } catch (err) {
        setLoading(false);
        // إذا فشل: err هو القيمة الممررة من rejectWithValue أو الخطأ المرمي
        console.error("addNewUser failed:", err);
        // لا تنفذ navigate هنا
        // يمكنك إظهار رسالة خطأ إضافية إذا أردت، لكن notify يتم عادة داخل thunk
      }
    },
  });

  let loginImage;
  try {
    loginImage = require("../assets/Logo.png");
  } catch (error) {
    loginImage = null;
  }

  // Country codes for the dropdown
  const countryCodes = [
    // { value: '+20', label: 'Egypt (+20)' },
    // { value: '+1', label: 'USA (+1)' },
    // { value: '+44', label: 'UK (+44)' },
    // { value: '+971', label: 'UAE (+971)' },
    { value: "+966", label: "KSA (+966)" },
    // { value: '+973', label: 'Bahrain (+973)' },
    // { value: '+974', label: 'Qatar (+974)' },
    // { value: '+968', label: 'Oman (+968)' },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Grid
        container
        spacing={0}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: 3,
          border: "1px solid #d0d0d0",
        }}
      >
        {/* Left Column - Sign Up Image */}
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
            flexDirection:"column",
            boxShadow: "-4px 0 10px rgba(0, 0, 0, 0.23)",
          }}
        >
          {loginImage ? (
            <img
              src={loginImage}
              alt="Login"
              style={{
                width: "50%",
                // height: '100%',
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h6" color="textSecondary">
                {t("Login Image Placeholder")}
              </Typography>
            </Box>
          )}
          <Typography variant="h6" color="textSecondary">
                          {t("productiveFamiliesMarket")}
                        </Typography>
        </Grid>

        {/* Right Column - Sign Up Form */}
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            backgroundColor: theme.palette.primary.mainLoginBackground,
            py: 6,
            px: { xs: 3, md: 6 },
          }}
        >
          <Box sx={{ maxWidth: 500, mx: "auto" }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 3,
                color: theme.palette.blackandWhite.main,
              }}
            >
              {t("Sign up")}
            </Typography>

            {/* Sign Up Form */}
            <form onSubmit={formik.handleSubmit}>
              {/* Full Name Field */}
              <CustomTextField
                fullWidth
                id="fullName"
                name="fullName"
                label={t("Full name")}
                required
                value={formik.values.fullName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.fullName && Boolean(formik.errors.fullName)
                }
                helperText={formik.touched.fullName && formik.errors.fullName}
                isRtl={isArabic}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor:
                      theme.palette.primary.mainTextFieldBackground,
                  },
                }}
              />

              {/* Email Field */}
              <CustomTextField
                fullWidth
                id="email"
                name="email"
                label={t("Email address")}
                required
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                isRtl={isArabic}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor:
                      theme.palette.primary.mainTextFieldBackground,
                  },
                }}
              />

              {/* Phone Number Field */}
              <Box sx={{ mb: 3 }}>
                <CustomTextField
                  fullWidth
                  id="phoneNumber"
                  name="phoneNumber"
                  label={t("Phone number")}
                  required
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.phoneNumber &&
                    Boolean(formik.errors.phoneNumber)
                  }
                  helperText={
                    formik.touched.phoneNumber && formik.errors.phoneNumber
                  }
                  isRtl={isArabic}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{ minWidth: 60, px: 1, color: "text.secondary" }}
                      >
                        +966
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor:
                        theme.palette.primary.mainTextFieldBackground,
                    },
                  }}
                />
              </Box>

              {/* Password Field */}
              <CustomTextField
                fullWidth
                id="password"
                name="password"
                label={t("Password")}
                required
                type={showPassword ? "text" : "password"}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                isRtl={isArabic}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor:
                      theme.palette.primary.mainTextFieldBackground,
                  },
                }}
              />

              {/* Confirm Password Field */}
              <CustomTextField
                fullWidth
                id="confirmPassword"
                name="confirmPassword"
                label={t("Confirm Password")}
                required
                type={showConfirmPassword ? "text" : "password"}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword)
                }
                helperText={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
                isRtl={isArabic}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor:
                      theme.palette.primary.mainTextFieldBackground,
                  },
                }}
              />

              <CustomTextField
                fullWidth
                id="address"
                name="address"
                label={t("common.address")}
                required
                type={"text"}
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                isRtl={isArabic}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor:
                      theme.palette.primary.mainTextFieldBackground,
                  },
                }}
              />
              <CustomTextField
                fullWidth
                id="locationurl"
                name="locationurl"
                label={t("userForm.fields.locationLink")}
                placeholder="https://maps.google.com/..."
                // required
                type={"text"}
                value={formik.values.locationurl}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.locationurl &&
                  Boolean(formik.errors.locationurl)
                }
                helperText={
                  formik.touched.locationurl && formik.errors.locationurl
                }
                isRtl={isArabic}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor:
                      theme.palette.primary.mainTextFieldBackground,
                  },
                }}
              />

              {/* Terms Agreement */}

              {/* Create Account Button */}
              <Button
                fullWidth
                variant="contained"
                type="submit"
                sx={{
                  py: 1.5,
                  mb: 3,
                  fontWeight: 600,
                  fontSize: "1rem",
                  borderRadius: "8px",
                }}
              >
                {loading ? (
                  <CircularProgress
                    sx={{ color: theme?.palette?.primary?.mainLoginBackground }}
                  />
                ) : (
                  t("Create account")
                )}
              </Button>

              {/* Login Link */}
              <Typography variant="body1" align="center" sx={{ mb: 3 }}>
                {t("Already have an account?")}{" "}
                <Box
                  onClick={() => {
                    navigate("/login");
                  }}
                  sx={{
                    display: "inline-block",
                    cursor: "pointer",
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                  }}
                >
                  {t("Login")}
                </Box>
              </Typography>
            </form>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SignUpScreen;
